import * as Sentry from "@sentry/react";
import { useState } from 'react';
import { Box, Button, Typography, Stack, Paper, Alert } from '@mui/material';
import axios from 'axios';

/**
 * Hidden debug page for testing Sentry error tracking.
 * Access via /debug/sentry
 */
function SentryTestPage() {
  const [status, setStatus] = useState(null);
  const [triggerRenderError, setTriggerRenderError] = useState(false);

  // 1. Render error - caught by ErrorBoundary
  if (triggerRenderError) {
    throw new Error('Test render error from SentryTestPage');
  }

  // 2. Event handler error - should be caught by Sentry global handler
  const handleClickError = () => {
    setStatus('Triggering click handler error...');
    throw new Error('Test click handler error');
  };

  // 3. Async/Promise rejection - should be caught by Sentry
  const handleAsyncError = async () => {
    setStatus('Triggering async error...');
    await new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Test async/promise rejection')), 100);
    });
  };

  // 4. Unhandled promise rejection
  const handleUnhandledRejection = () => {
    setStatus('Triggering unhandled promise rejection...');
    new Promise((_, reject) => {
      reject(new Error('Test unhandled promise rejection'));
    });
  };

  // 5. Network error - API call to non-existent endpoint
  const handleNetworkError = async () => {
    setStatus('Triggering network error...');
    try {
      await axios.get('/api/this-endpoint-does-not-exist/');
    } catch (error) {
      // This is the problem - errors caught here don't go to Sentry
      // unless we explicitly capture them
      console.error('Network error (caught, not sent to Sentry):', error);
      setStatus('Network error caught locally - check if it appears in Sentry');
    }
  };

  // 6. Network error WITH Sentry capture
  const handleNetworkErrorWithCapture = async () => {
    setStatus('Triggering network error with Sentry capture...');
    try {
      await axios.get('/api/this-endpoint-does-not-exist/');
    } catch (error) {
      Sentry.captureException(error);
      setStatus('Network error captured and sent to Sentry');
    }
  };

  // 7. Manual Sentry message
  const handleManualCapture = () => {
    Sentry.captureMessage('Test manual message from SentryTestPage', 'info');
    setStatus('Manual message sent to Sentry');
  };

  // 8. Manual Sentry exception
  const handleManualException = () => {
    Sentry.captureException(new Error('Test manual exception capture'));
    setStatus('Manual exception sent to Sentry');
  };

  return (
    <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Sentry Test Page
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        This page is for testing Sentry error tracking. Errors triggered here will be sent to Sentry (in production only).
        <br />
        <strong>NODE_ENV:</strong> {process.env.NODE_ENV}
        <br />
        <strong>Sentry enabled:</strong> {process.env.NODE_ENV === 'production' ? 'Yes' : 'No (dev mode)'}
      </Alert>

      {status && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {status}
        </Alert>
      )}

      <Stack spacing={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>React Errors</Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="error"
              onClick={() => setTriggerRenderError(true)}
            >
              Trigger Render Error
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleClickError}
            >
              Trigger Click Error
            </Button>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Async/Promise Errors</Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="warning"
              onClick={handleAsyncError}
            >
              Trigger Async Error
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleUnhandledRejection}
            >
              Unhandled Rejection
            </Button>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Network Errors</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Network errors caught in try/catch don't automatically go to Sentry - they need explicit capture.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="info"
              onClick={handleNetworkError}
            >
              Network Error (no capture)
            </Button>
            <Button
              variant="contained"
              color="info"
              onClick={handleNetworkErrorWithCapture}
            >
              Network Error (with capture)
            </Button>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Manual Sentry Capture</Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={handleManualCapture}
            >
              Send Test Message
            </Button>
            <Button
              variant="outlined"
              onClick={handleManualException}
            >
              Send Test Exception
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}

export default SentryTestPage;
